import type { CssVarMap } from '../theme';
import type { Theme } from './types';
import { registerTheme } from './registry';

/**
 * Black-Gold override layer.
 *
 * Originally exported from `src/blackGold.ts` as a bare `CssVarMap`; refactored
 * into a Theme plugin while keeping `BLACK_GOLD_TOKENS` as a named export so
 * downstream tests (and any external consumer pinning the symbol) keep
 * working.
 *
 * The tokens override same-named keys from `@axi/tokens` so the browser-level
 * cascade picks the override. New namespaces (`--axi-font-*`, `--axi-space-*`,
 * `--axi-radius-*`, `--axi-shadow-*`, `--gold-glow-*`) are introduced for
 * tokens the provider doesn't ship.
 */
export const BLACK_GOLD_TOKENS: CssVarMap = {
  /* ---------------- Background (deeper blacks to match reference) ---------------- */
  '--bg-page': '#0F1114',
  '--bg-surface': '#17191C',
  '--bg-elevated': '#202225',
  '--bg-sidebar': '#16181B',

  /* ---------------- Primary (Gold) — warmer ---------------- */
  '--primary': '#E5B94A',
  '--primary-hover': '#F0CB6E',
  '--primary-active': '#D6A23A',

  /* ---------------- Text ---------------- */
  '--text': '#F1F5F9',
  '--text-secondary': '#94A3B8',
  '--text-muted': '#64748B',
  '--text-inverse': '#161718',

  /* ---------------- Border ---------------- */
  '--panel-border': '#334155',
  '--line': '#2A2D2F',

  /* ---------------- Status ---------------- */
  '--green': '#22C55E',
  '--amber': '#F59E0B',
  '--red': '#EF4444',

  /* ---------------- Gold Glow (decorative) ---------------- */
  '--gold-glow-strong': 'rgba(229,185,74,0.45)',
  '--gold-glow-soft': 'rgba(229,185,74,0.10)',
  '--gold-glow-fade': 'rgba(229,185,74,0.00)',

  /* ---------------- Typography ---------------- */
  '--axi-font-family':
    'system-ui, -apple-system, "PingFang SC", "Microsoft YaHei", "Segoe UI", Roboto, sans-serif',
  '--axi-font-size-xs': '12px',
  '--axi-font-size-sm': '14px',
  '--axi-font-size-md': '16px',
  '--axi-font-size-lg': '20px',
  '--axi-font-size-xl': '24px',
  '--axi-font-size-2xl': '28px',
  '--axi-font-size-metric': '36px',
  '--axi-font-weight-normal': '400',
  '--axi-font-weight-medium': '500',
  '--axi-font-weight-semibold': '600',
  '--axi-font-weight-bold': '700',
  '--axi-line-height-tight': '1.2',
  '--axi-line-height-normal': '1.5',
  '--axi-line-height-relaxed': '1.7',

  /* ---------------- Spacing (8px base) ---------------- */
  '--axi-space-1': '4px',
  '--axi-space-2': '8px',
  '--axi-space-3': '12px',
  '--axi-space-4': '16px',
  '--axi-space-5': '24px',
  '--axi-space-6': '32px',
  '--axi-space-8': '48px',

  /* ---------------- Border Radius ---------------- */
  '--axi-radius-sm': '6px',
  '--axi-radius-md': '10px',
  '--axi-radius-lg': '12px',
  '--axi-radius-xl': '16px',
  '--axi-radius-full': '9999px',

  /* ---------------- Shadow ---------------- */
  '--axi-shadow-sm': '0 1px 2px 0 rgb(0 0 0 / 0.08)',
  '--axi-shadow-md':
    '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
  '--axi-shadow-lg':
    '0 10px 15px -3px rgb(0 0 0 / 0.15), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
  '--axi-shadow-gold': '0 0 0 1px rgba(229, 185, 74, 0.18)'
};

export const blackGoldTheme: Theme = {
  slug: 'dark-gold',
  label: 'Black-Gold',
  description: '黑金品牌色主题,默认 dashboard 主题.',
  tokens: BLACK_GOLD_TOKENS
};

// Side-effect: register this theme with the global registry at import time.
registerTheme(blackGoldTheme);